// Volumetric lighting — ray marched light scattering at quarter resolution
// Engine feature: opt-in. Creates light shafts, atmospheric fog, god rays.

import type { GPU } from "./gpu.js";
import { mat4Invert, type Mat4 } from "./math3d.js";
import type { PointLightDef } from "./lighting.js";

const VOL_SHADER = /* wgsl */ `
const MAX_POINT_LIGHTS = 4u; // fewer than lighting pass — volumetrics are expensive

struct PointLight {
  pos: vec3f, radius: f32,
  color: vec3f, intensity: f32,
};

struct VolParams {
  invViewProj: mat4x4f,
  camPos: vec3f, maxDist: f32,
  sunDir: vec3f, sunIntensity: f32,
  voxelMin: vec3f, density: f32,
  voxelMax: vec3f, strength: f32,
  screenSize: vec2f, pointLightCount: u32, _pad: u32,
  pointLights: array<PointLight, 4>,
};

@group(0) @binding(0) var voxelVolume: texture_3d<f32>;
@group(0) @binding(1) var voxelSampler: sampler;
@group(0) @binding(2) var depthTex: texture_depth_2d;
@group(0) @binding(3) var volOut: texture_storage_2d<rgba16float, write>;
@group(0) @binding(4) var<uniform> params: VolParams;

fn worldToVoxelUV(wp: vec3f) -> vec3f {
  return (wp - params.voxelMin) / (params.voxelMax - params.voxelMin);
}

// Shadow test through voxel volume — must reach far enough to hit walls/ceilings
fn shadowTest(origin: vec3f, dir: vec3f, maxDist: f32) -> f32 {
  let voxelExtent = params.voxelMax - params.voxelMin;
  let voxelSize = max(voxelExtent.x, max(voxelExtent.y, voxelExtent.z)) / 256.0;

  var vis = 1.0;
  var d = voxelSize * 2.0;
  // Use progressively larger steps to cover the full scene extent
  for (var i = 0; i < 24; i++) {
    if (vis < 0.1 || d > maxDist) { break; }
    let uv = worldToVoxelUV(origin + dir * d);
    if (any(uv < vec3f(0.0)) || any(uv > vec3f(1.0))) { break; }
    // Sample at mip proportional to step distance for efficiency
    let mip = clamp(log2(d / voxelSize) - 2.0, 0.0, 3.0);
    let s = textureSampleLevel(voxelVolume, voxelSampler, uv, mip);
    vis *= (1.0 - s.a);
    // Step grows with distance: small near, large far
    d += max(voxelSize * 2.0, d * 0.3);
  }
  return vis;
}

// Henyey-Greenstein phase function
fn phase(cosTheta: f32, g: f32) -> f32 {
  let g2 = g * g;
  let denom = 1.0 + g2 - 2.0 * g * cosTheta;
  return (1.0 - g2) / (4.0 * 3.14159 * pow(denom, 1.5));
}

fn hash(p: vec2f) -> f32 {
  return fract(sin(dot(p, vec2f(127.1, 311.7))) * 43758.5453);
}

@compute @workgroup_size(8, 8)
fn volMain(@builtin(global_invocation_id) gid: vec3u) {
  let volSize = vec2i(params.screenSize) / 4;
  let coord = vec2i(gid.xy);
  if (coord.x >= volSize.x || coord.y >= volSize.y) { return; }

  // Reconstruct ray from screen position
  let uv = (vec2f(coord) + 0.5) / vec2f(volSize);
  let ndc = vec2f(uv.x * 2.0 - 1.0, 1.0 - uv.y * 2.0);
  let nearClip = params.invViewProj * vec4f(ndc, 0.0, 1.0);
  let farClip = params.invViewProj * vec4f(ndc, 1.0, 1.0);
  let near = nearClip.xyz / nearClip.w;
  let far = farClip.xyz / farClip.w;
  let rayDir = normalize(far - near);

  // Depth at this pixel (sample from full-res depth buffer)
  let depthCoord = coord * 4;
  let depth = textureLoad(depthTex, depthCoord, 0);
  var maxRayDist = params.maxDist;
  if (depth < 0.99999) {
    // Reconstruct world position from depth to get distance
    let depthUV = (vec2f(depthCoord) + 0.5) / params.screenSize;
    let depthNdc = vec4f(depthUV.x * 2.0 - 1.0, 1.0 - depthUV.y * 2.0, depth, 1.0);
    let depthWorld = params.invViewProj * depthNdc;
    let geoPos = depthWorld.xyz / depthWorld.w;
    maxRayDist = min(maxRayDist, length(geoPos - params.camPos));
  }

  // Jitter start to break banding
  let jitter = hash(vec2f(coord)) * 0.5;

  let steps = 48u;
  let stepSize = maxRayDist / f32(steps);
  var accumColor = vec3f(0.0);
  var accumTransmittance = 1.0;

  let sunDir = normalize(params.sunDir);
  let viewDotSun = dot(rayDir, sunDir);

  for (var i = 0u; i < steps; i++) {
    if (accumTransmittance < 0.01) { break; }

    let t = (f32(i) + jitter) * stepSize;
    let samplePos = params.camPos + rayDir * t;

    // Check if inside solid geometry (skip) — only testable inside voxel bounds
    let vuv = worldToVoxelUV(samplePos);
    let inBounds = all(vuv >= vec3f(0.0)) && all(vuv <= vec3f(1.0));
    if (inBounds) {
      let voxel = textureSampleLevel(voxelVolume, voxelSampler, vuv, 0.0);
      if (voxel.a > 0.5) { continue; }
    }

    let scatterAmount = params.density * stepSize;

    // Sun contribution
    if (params.sunIntensity > 0.001) {
      let sunVis = shadowTest(samplePos, sunDir, 50.0);
      let sunPhase = phase(viewDotSun, 0.6);
      accumColor += vec3f(1.0, 0.95, 0.9) * params.sunIntensity * sunVis * sunPhase * scatterAmount * accumTransmittance;
    }

    // Point lights
    for (var p = 0u; p < params.pointLightCount; p++) {
      let pl = params.pointLights[p];
      let toLight = pl.pos - samplePos;
      let dist = length(toLight);
      if (dist > pl.radius) { continue; }

      let Lp = toLight / dist;
      let atten = 1.0 - (dist / pl.radius);
      let falloff = atten * atten;
      let lightVis = shadowTest(samplePos, Lp, dist);
      let lightPhase = phase(dot(rayDir, Lp), 0.3);

      accumColor += pl.color * pl.intensity * falloff * lightVis * lightPhase * scatterAmount * accumTransmittance;
    }

    accumTransmittance *= exp(-scatterAmount);
  }

  textureStore(volOut, coord, vec4f(accumColor * params.strength, 1.0));
}
`;

export type VolumetricConfig = {
  density: number;
  strength: number;
  maxDist: number;
};

export type VolumetricParams = {
  viewProj: Float32Array;
  camPos: [number, number, number];
  sunDir: [number, number, number];
  sunIntensity: number;
  voxelMin: [number, number, number];
  voxelMax: [number, number, number];
  pointLights: PointLightDef[];
};

export class Volumetrics {
  output: GPUTexture;

  private pipeline: GPUComputePipeline;
  private bindGroup: GPUBindGroup;
  private uniformBuffer: GPUBuffer;
  private volW: number;
  private volH: number;
  config: VolumetricConfig;

  constructor(private gpu: GPU, voxelVolume: GPUTexture, voxelSampler: GPUSampler, depthTexture: GPUTexture, config: VolumetricConfig = { density: 0.015, strength: 1.0, maxDist: 50 }) {
    const { device, canvas } = gpu;
    this.config = config;
    this.volW = Math.ceil(canvas.width / 4);
    this.volH = Math.ceil(canvas.height / 4);

    this.output = device.createTexture({
      size: [this.volW, this.volH],
      format: "rgba16float",
      usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING,
    });

    // Uniforms: invViewProj(16) + camPos+maxDist(4) + sunDir+sunInt(4) + voxelMin+density(4) + voxelMax+strength(4) + screenSize+counts(4) + 4 lights × 8 = 68 floats = 272 bytes
    this.uniformBuffer = device.createBuffer({ size: 288, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });

    const module = device.createShaderModule({ code: VOL_SHADER });
    const layout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "float", viewDimension: "3d" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, sampler: {} },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "depth" } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: "write-only", format: "rgba16float" } },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
      ],
    });

    this.pipeline = device.createComputePipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [layout] }),
      compute: { module, entryPoint: "volMain" },
    });

    this.bindGroup = device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: voxelVolume.createView({ dimension: "3d" }) },
        { binding: 1, resource: voxelSampler },
        { binding: 2, resource: depthTexture.createView() },
        { binding: 3, resource: this.output.createView() },
        { binding: 4, resource: { buffer: this.uniformBuffer } },
      ],
    });
  }

  compute(encoder: GPUCommandEncoder, params: VolumetricParams): void {
    const { device, canvas } = this.gpu;
    const invViewProj = mat4Invert(params.viewProj);

    const lights = params.pointLights.slice(0, 4);
    const data = new Float32Array(36 + lights.length * 8);

    // invViewProj (16 floats)
    data.set(invViewProj, 0);
    // camPos + maxDist
    data[16] = params.camPos[0]; data[17] = params.camPos[1]; data[18] = params.camPos[2]; data[19] = this.config.maxDist;
    // sunDir + sunIntensity
    data[20] = params.sunDir[0]; data[21] = params.sunDir[1]; data[22] = params.sunDir[2]; data[23] = params.sunIntensity;
    // voxelMin + density
    data[24] = params.voxelMin[0]; data[25] = params.voxelMin[1]; data[26] = params.voxelMin[2]; data[27] = this.config.density;
    // voxelMax + strength
    data[28] = params.voxelMax[0]; data[29] = params.voxelMax[1]; data[30] = params.voxelMax[2]; data[31] = this.config.strength;
    // screenSize + counts
    data[32] = canvas.width; data[33] = canvas.height;
    const uintView = new Uint32Array(data.buffer);
    uintView[34] = lights.length; uintView[35] = 0;

    // Point lights
    for (let i = 0; i < lights.length; i++) {
      const pl = lights[i];
      const o = 36 + i * 8;
      data[o] = pl.pos[0]; data[o+1] = pl.pos[1]; data[o+2] = pl.pos[2]; data[o+3] = pl.radius;
      data[o+4] = pl.color[0]; data[o+5] = pl.color[1]; data[o+6] = pl.color[2]; data[o+7] = pl.intensity;
    }

    device.queue.writeBuffer(this.uniformBuffer, 0, data);

    const pass = encoder.beginComputePass();
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.dispatchWorkgroups(Math.ceil(this.volW / 8), Math.ceil(this.volH / 8));
    pass.end();
  }
}
